/* pg-home.js — the homepage banner rotator (sections/pg-home-hero.liquid).
 *
 * A plain crossfade. There was a jigsaw-assembly transition here — six brand
 * piece shapes carrying fragments of the incoming banner, flying into place —
 * and it was removed on 2026-08-05 at the operator's call: it did not read as
 * intended on the real designed banners, and a rotator whose job is to show
 * three finished pieces of artwork does not need a trick between them. It also
 * took the last horizontal-overflow offender off the page with it. Don't
 * reintroduce it without a reason better than "it would be clever".
 *
 * Responsibilities: crossfade slides, drive the pips' progress bar, and honour
 * pause — button (WCAG 2.2.2), hover, focus, hidden tab. Swipe on touch.
 * Everything is defensive; a missing piece disables that feature only.
 *
 * Reduced motion (prefers-reduced-motion: reduce): the banner opens PAUSED and
 * never advances by itself; arrows, pips, swipe and the play button still work
 * by hand, and pressing play is the visitor asking for it, so it then runs. The
 * crossfade is switched off in pg-home.css. The setting is watched while the
 * page is open. Until 2026-10-03 the timer ignored it (the older pg-hero-rotator
 * did not), which also made every homepage screenshot a coin toss.
 */
(() => {
  "use strict";
  if (customElements.get("pg-home-hero")) return;

  class PGHomeHero extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll(".pgh__slide")];
      this.pips = [...this.querySelectorAll(".pgh__pip")];
      this.idx = 0;
      this.timer = 0;
      this.interval = Math.max(4, parseFloat(this.dataset.interval) || 7) * 1000;
      this.style.setProperty("--pgh-interval", this.interval / 1000 + "s");

      if (this.slides.length > 1) {
        this._wire();
        this._watchMotion();
        this._arm();
      }
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.preTimer);
      document.removeEventListener("visibilitychange", this._vis);
      if (this._mq && this._mqChange) {
        if (this._mq.removeEventListener) this._mq.removeEventListener("change", this._mqChange);
        else if (this._mq.removeListener) this._mq.removeListener(this._mqChange);
      }
    }

    // One place sets the paused state, so the attribute (which the CSS and
    // _arm read), the button's pressed state and its label cannot disagree.
    _setPaused(on, label) {
      this.toggleAttribute("data-paused", on);
      const pause = this.querySelector("[data-pgh-pause]");
      if (pause) {
        pause.setAttribute("aria-pressed", String(on));
        pause.setAttribute("aria-label", on ? label || "Resume slideshow" : "Pause slideshow");
      }
      if (on) clearTimeout(this.timer);
      else this._arm();
    }

    // Reduced motion pauses the banner; it does not take the controls away.
    // _motionPaused marks a pause WE made, so turning the setting off again
    // resumes only that one and never undoes a pause the visitor pressed.
    _watchMotion() {
      if (!window.matchMedia) return;
      this._mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      this._mqChange = () => {
        if (this._mq.matches) {
          if (!this.hasAttribute("data-paused")) {
            this._motionPaused = true;
            this._setPaused(true, "Play slideshow");
          }
        } else if (this._motionPaused) {
          this._motionPaused = false;
          this._setPaused(false);
        }
      };
      if (this._mq.addEventListener) this._mq.addEventListener("change", this._mqChange);
      else if (this._mq.addListener) this._mq.addListener(this._mqChange); // Safari before 14
      this._mqChange();
    }

    _wire() {
      this.querySelector("[data-pgh-prev]")?.addEventListener("click", () => this.go(this.idx - 1));
      this.querySelector("[data-pgh-next]")?.addEventListener("click", () => this.go(this.idx + 1));
      for (const p of this.pips) {
        p.addEventListener("click", () => this.go(parseInt(p.dataset.pghGoto, 10)));
      }
      const pause = this.querySelector("[data-pgh-pause]");
      if (pause) {
        pause.addEventListener("click", () => {
          // The visitor's own press outranks the reduced-motion pause.
          this._motionPaused = false;
          this._setPaused(!this.hasAttribute("data-paused"));
        });
      }
      // A visitor reaching for the banner is about to change slide: fetch both
      // neighbours now so an arrow press never crossfades onto an empty plate.
      const warm = () => { this._hydrateAround(this.idx); };
      this.addEventListener("pointerenter", warm, { once: true });
      this.addEventListener("focusin", warm, { once: true });
      this.addEventListener("touchstart", warm, { once: true, passive: true });
      this.addEventListener("pointerenter", () => clearTimeout(this.timer));
      this.addEventListener("pointerleave", () => this._arm());
      this.addEventListener("focusin", () => clearTimeout(this.timer));
      this.addEventListener("focusout", (e) => {
        if (!(e.relatedTarget instanceof Node) || !this.contains(e.relatedTarget)) this._arm();
      });
      this._vis = () => { if (document.hidden) clearTimeout(this.timer); else this._arm(); };
      document.addEventListener("visibilitychange", this._vis);

      // Swipe: a clean horizontal flick changes slide; vertical scrolling wins.
      let sx = 0, sy = 0;
      this.addEventListener("pointerdown", (e) => { sx = e.clientX; sy = e.clientY; }, { passive: true });
      this.addEventListener("pointerup", (e) => {
        const dx = e.clientX - sx, dy = e.clientY - sy;
        if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.5) this.go(this.idx + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }

    _arm() {
      clearTimeout(this.timer);
      clearTimeout(this.preTimer);
      if (this.hasAttribute("data-paused") || document.hidden) return;
      this.timer = setTimeout(() => this.go(this.idx + 1), this.interval);
      // The next slide's picture is fetched 3 seconds before it is due, not on
      // arrival (see pg-home-hero.liquid: only slide 1 ships a live src).
      this.preTimer = setTimeout(() => this._hydrate(this.slides[(this.idx + 1) % this.slides.length]), Math.max(0, this.interval - 3000));
    }

    // Copy a deferred slide's data-src / data-srcset onto the live attributes.
    // Idempotent: a slide already hydrated has no data-pgh-deferred left.
    _hydrate(slide) {
      if (!slide) return;
      const img = slide.querySelector("img[data-pgh-deferred]");
      if (!img) return;
      for (const s of slide.querySelectorAll("source[data-srcset]")) {
        s.setAttribute("srcset", s.getAttribute("data-srcset"));
        s.removeAttribute("data-srcset");
      }
      if (img.hasAttribute("data-srcset")) {
        img.setAttribute("srcset", img.getAttribute("data-srcset"));
        img.removeAttribute("data-srcset");
      }
      if (img.hasAttribute("data-src")) {
        img.setAttribute("src", img.getAttribute("data-src"));
        img.removeAttribute("data-src");
      }
      img.removeAttribute("data-pgh-deferred");
      img.loading = "eager";
    }

    _hydrateAround(i) {
      const n = this.slides.length;
      this._hydrate(this.slides[(i + 1) % n]);
      this._hydrate(this.slides[(i - 1 + n) % n]);
    }

    _restartPip() {
      this.pips.forEach((p, n) => {
        p.classList.toggle("is-on", n === this.idx);
        if (n === this.idx) {
          // Replacing the node restarts the CSS progress animation from zero.
          const bar = p.querySelector("i");
          if (bar) bar.replaceWith(bar.cloneNode(true));
        }
      });
    }

    go(n) {
      if (this.slides.length < 2) return;
      const next = ((n % this.slides.length) + this.slides.length) % this.slides.length;
      if (next === this.idx) { this._arm(); return; }

      const outgoing = this.slides[this.idx];
      const incoming = this.slides[next];
      this._hydrate(incoming);
      this.idx = next;
      this._restartPip();

      outgoing.classList.remove("is-active");
      outgoing.setAttribute("aria-hidden", "true");
      outgoing.setAttribute("inert", "");
      incoming.classList.add("is-active");
      incoming.removeAttribute("aria-hidden");
      incoming.removeAttribute("inert");
      this._arm();
      this._announceSlide();
    }

    // Anything that wants to follow the banner listens for this. The hero never
    // reaches into another section's DOM — see the note in pg-home-hero.liquid.
    _announceSlide() {
      document.dispatchEvent(
        new CustomEvent("pg:hero-slide", { detail: { index: this.idx, section: this.dataset.sectionId || null } })
      );
    }
  }

  customElements.define("pg-home-hero", PGHomeHero);

  /* ------------------------------------------------------------------ *
   * First discovery card follows the banner.
   *
   * ⚠ FAIL-SAFE BY DESIGN, and that is the whole trick. Every step bails
   * quietly: no card on the page, no data script, a slide with no collection
   * set — each leaves the card exactly as the server rendered it. The card is
   * always correct on its own; this only ever upgrades it. That is why it can
   * be wired to a section it knows nothing about without becoming a liability.
   * ------------------------------------------------------------------ */
  (function syncDiscoveryCard() {
    const card = document.querySelector(".pg-discovery-grid .pg-discovery-card");
    if (!card) return;
    const data = document.querySelector('script[id^="pgHeroCards-"]');
    if (!data) return;

    let slides;
    try {
      slides = JSON.parse(data.textContent);
    } catch {
      return; // Malformed data must not break the banner or the card.
    }
    if (!Array.isArray(slides) || !slides.length) return;

    const parts = () => ({
      h3: card.querySelector(".pg-discovery-card-title h3"),
      titleLink: card.querySelector(".pg-discovery-card-title"),
      hero: card.querySelector(".pg-discovery-thumb--hero"),
      link: card.querySelector(".pg-discovery-link"),
    });

    // What the server rendered — restored whenever a slide sets no collection,
    // so the card can never be left showing the previous slide's range.
    const p0 = parts();
    const fallback = {
      on: true,
      title: p0.h3 ? p0.h3.textContent : "",
      link: p0.titleLink ? p0.titleLink.getAttribute("href") : "",
      imgHref: p0.hero ? p0.hero.getAttribute("href") : "",
      img: p0.hero && p0.hero.querySelector("img") ? p0.hero.querySelector("img").getAttribute("src") : "",
      linkText:
        p0.link && p0.link.childNodes[0] && p0.link.childNodes[0].nodeType === 3
          ? p0.link.childNodes[0].nodeValue.replace(/\s+$/, "")
          : "",
    };

    function apply(d) {
      if (!d) return;
      const p = parts();
      if (p.h3 && d.title) p.h3.textContent = d.title;
      if (p.titleLink && d.link) p.titleLink.setAttribute("href", d.link);
      if (p.hero) p.hero.setAttribute("href", d.imgHref || d.link || "#");
      const img = p.hero && p.hero.querySelector("img");
      if (img && d.img) {
        // The srcset describes the OLD image; leaving it lets the browser pick a
        // rendition of the puzzle we just replaced.
        img.removeAttribute("srcset");
        img.setAttribute("src", d.img);
        img.setAttribute("alt", d.title || "");
      }
      if (p.link && d.link) p.link.setAttribute("href", d.link);
      if (p.link && d.linkText && p.link.childNodes[0] && p.link.childNodes[0].nodeType === 3) {
        p.link.childNodes[0].nodeValue = d.linkText + " ";
      }
    }

    document.addEventListener("pg:hero-slide", (e) => {
      const d = slides[e.detail && e.detail.index];
      apply(d && d.on ? d : fallback);
    });

    /* ⚠ APPLY THE OPENING SLIDE NOW — pg:hero-slide only fires on a slide CHANGE.
       Without this the card sat on whatever the server rendered until the first
       rotation, so EVERY visitor met the opening banner beside the wrong card and
       the mismatch corrected itself ~7 seconds later, if they were still there.
       Found live on 2026-08-07: the Wasgij Advent banner was paired with "House
       of Puzzles". The most valuable impression on the site was the one broken one.

       ⚠ Read the index from the DOM, don't assume 0 — the pill and the slide are
       the same source of truth the customer is looking at, so this cannot drift
       from a future change to which slide opens.

       ⚠ And do it here, not by dispatching from the hero's connectedCallback:
       customElements.define() upgrades synchronously, so the element can connect
       and announce BEFORE this listener is registered, and the event is missed. */
    const slideEls = document.querySelectorAll("pg-home-hero .pgh__slide");
    let startIdx = 0;
    for (let i = 0; i < slideEls.length; i++) {
      if (slideEls[i].classList.contains("is-active")) { startIdx = i; break; }
    }
    const first = slides[startIdx];
    if (first && first.on) apply(first);
  })();
})();
